import { toHumanString } from 'human-readable-numbers'
import { get } from 'lodash'
import accounting from 'accounting'

export const NON_OFFERED_PLANS = ['FA-COMPED-STANDARD', 'FA-EMPLOYEE', 'WA-FA-ONLY-BACKER', 'WA-FA-BUNDLE-BACKER', 'WA-FA-TEAM-BACKER']

export const ALL_PLANS_BY_SKU = ['FA-LITE', 'FA-LITE-PLUS', 'FA-STANDARD', 'FA-STANDARD-PLUS', 'FA-MAX', 'FA-MAX-PLUS']

export const PLAN_COLOR_BY_SKU = {
  'FA-LITE-PLUS': 'teal',
  'FA-STANDARD-PLUS': 'teal',
  'FA-MAX-PLUS': 'teal',
  'FA-LITE': 'yellow',
  'FA-STANDARD': 'yellow',
  'FA-MAX': 'yellow'
}

export const PLAN_ICONSTYLE_BY_SKU = {
  'FA-LITE': 'custom',
  'FA-LITE-PLUS': 'custom',
  'FA-STANDARD': 'custom',
  'FA-STANDARD-PLUS': 'custom',
  'FA-MAX': 'custom',
  'FA-MAX-PLUS': 'custom'
}

// Plans page descriptions
export const PLAN_DESCRIPTION_PRIMARY_BY_SKU = {
  'FA-LITE': 'Cloud-hosted Pro icons, served online via Kits.',
  'FA-LITE-PLUS': 'Everything in Pro Lite, plus all Pro+ icon packs.',
  'FA-STANDARD': 'The full Font Awesome Pro icon library and toolkit.',
  'FA-STANDARD-PLUS': 'Everything in Pro, plus all Pro+ icon packs.',
  'FA-MAX': 'Built for teams with high traffic and usage.',
  'FA-MAX-PLUS': 'Everything in Pro Max, plus all Pro+ icon packs.'
}

export const PLAN_DESCRIPTION_SECONDARY_BY_SKU = {
  'FA-LITE': 'Fast setup with no downloads required.',
  'FA-LITE-PLUS': 'Online-only access with higher usage limits.',
  'FA-STANDARD': 'Standard usage limits with online and offline access.',
  'FA-STANDARD-PLUS': 'Higher usage limits across projects and teams.',
  'FA-MAX': 'Expanded limits, seats, and bandwidth.',
  'FA-MAX-PLUS': 'Our highest limits for teams at scale.'
}

// Modal (and other compact contexts) descriptions
export const PLAN_DESCRIPTION_BY_SKU = {
  'FA-LITE': 'Cloud-hosted Pro icons, served online via Kits.',
  'FA-LITE-PLUS': 'Cloud-hosted Pro icons plus Pro+ packs.',
  'FA-STANDARD': 'The full Font Awesome Pro library and toolkit.',
  'FA-STANDARD-PLUS': 'The full Pro library and toolkit plus Pro+ packs.',
  'FA-MAX': 'Built for teams with high traffic and usage.',
  'FA-MAX-PLUS': 'Built for teams with high usage. Includes Pro and Pro+ packs.'
}

export const ALL_PLAN_SLUGS = ['pro-lite', 'pro-lite-plus', 'pro', 'pro-plus', 'pro-max', 'pro-max-plus']

export const ALL_PLANS_BY_PROPER_NAME = ['Pro Lite', 'Pro Lite+', 'Pro', 'Pro+', 'Pro Max', 'Pro Max+']

export function getAutoAppliedDiscounts() {
  return AUTO_APPLIED_DISCOUNT_CODES
}

export function getKickstarterDiscounts() {
  return AUTO_APPLIED_KICKSTARTER_DISCOUNT_CODES
}

export function getPromoCodes() {
  return PROMO_CODES
}

// Put any active promo codes here. Any promo code on the query string will take precidence.
// If we have a promo code we want applied across all plans, you'll want to make sure it's the
// only one in this list.
export const AUTO_APPLIED_DISCOUNT_CODES = []

// Used for Kickstarter backers. These discounts are applied automatically.
export const AUTO_APPLIED_KICKSTARTER_DISCOUNT_CODES = []

// This is used when we want to show a promo code to certain accounts
export const PROMO_CODES = {
  0: { id: null, code: null }
}

// This is used when we want to show a promo code to certain accounts
export function shouldHavePromoCode(account, subscription) {
  // return account && get(subscription, 'status', '') === 'active' && get(subscription, 'type', '') !== 'free'
  return false
}

// This is used when we want to show a promo code to certain accounts
export function getDiscountPercent(account, subscription) {
  return '0'
}

export const HANDLE_FUNCTIONS = {
  asText(params = {}) {
    const { field } = params

    return (plan) => {
      return { included: true, decorator: plan[field] }
    }
  },

  asBoolean(params = {}) {
    const { field } = params

    return (plan) => {
      return { included: plan[field] }
    }
  },

  asUnlimitedNumber(params = {}) {
    const { field } = params

    return (plan) => {
      const number = plan[field]

      return { included: number !== 0, decorator: this.unlimitedNumberFormatted(number) }
    }
  },

  asDeepFieldMatch(params = {}) {
    const { field, match } = params

    return (plan) => {
      const value = get(plan, field.split(' '), false)

      return { included: match.includes(value) }
    }
  },

  asHumanReadableNumber(params = {}) {
    const { field } = params

    return (plan) => {
      const number = plan[field]

      return { included: number !== 0, decorator: toHumanString(number) }
    }
  },

  asDataPerMonth(params = {}) {
    const { field, numberSuffix = 'GB' } = params

    return (plan) => {
      const total = plan[field]

      return { included: total !== 0, decorator: `${total} ${numberSuffix}` }
    }
  },

  asPriceWithSuffix(params = {}) {
    const { field, suffix, numberPrecision } = params

    return (plan) => {
      const number = get(plan, field, { amount: 0 })

      if (number !== null) {
        return { included: true, decorator: `${this.formatMoney(number.amount, numberPrecision)}${suffix}` }
      } else {
        return { included: false }
      }
    }
  },

  unlimitedNumberFormatted(number) {
    if (number < 0) {
      return 'Unlimited'
    } else {
      return number
    }
  },

  formatMoney(money, precision = 0) {
    const formattedMoney = (money || 0) / 100

    return accounting.formatMoney(formattedMoney, { precision: precision })
  }
}

export const TABLE_ROWS = [
  [
    {
      featureName: 'Plan',
      featureDescription: 'Plan name',
      featureIcon: 'faFontAwesome',
      handleFunction: HANDLE_FUNCTIONS.asText({ field: 'name' })
    },
    {
      featureName: 'Yearly Price',
      featureDescription: 'Annual price',
      featureIcon: 'faCreditCard',
      handleFunction: HANDLE_FUNCTIONS.asPriceWithSuffix({ field: 'price', suffix: '' })
    },
    {
      featureName: 'Team Seats',
      featureDescription: 'Starting number of seats included in your team',
      featureIcon: 'faUserGroup',
      handleFunction: HANDLE_FUNCTIONS.asUnlimitedNumber({ field: 'seats' })
    },
    {
      featureName: 'Project Kits',
      featureDescription: 'Kits included for your projects',
      featureIcon: 'faSuitcase',
      handleFunction: HANDLE_FUNCTIONS.asUnlimitedNumber({ field: 'kits' })
    },
    {
      featureName: 'Icon Upload',
      featureDescription: 'Upload and use your own icons',
      featureIcon: 'faCloudArrowUp',
      handleFunction: HANDLE_FUNCTIONS.asUnlimitedNumber({ field: 'customIcons' })
    },
    {
      featureName: 'Pageviews',
      featureDescription: 'Monthly Kit pageviews included',
      featureIcon: 'faChartSimple',
      handleFunction: HANDLE_FUNCTIONS.asHumanReadableNumber({ field: 'monthlyPageviews' })
    },
    {
      featureName: 'Bandwidth',
      featureDescription: 'Monthly package bandwidth included',
      featureIcon: 'faGauge',
      handleFunction: HANDLE_FUNCTIONS.asDataPerMonth({ field: 'monthlyBandwidth', numberSuffix: 'GB' })
    }
  ],
  [
    {
      featureName: 'Additional Seats',
      featureDescription: 'Price per additional team member',
      featureIcon: 'faCircleUser',
      handleFunction: HANDLE_FUNCTIONS.asPriceWithSuffix({ field: 'seatUpgradePrice', suffix: '', numberPrecision: 0 })
    },
    {
      featureName: '+250K Pageviews',
      featureDescription: 'Price per extra 250k pageviews per month',
      featureIcon: 'faChartSimple',
      handleFunction: HANDLE_FUNCTIONS.asPriceWithSuffix({ field: 'overagePageviewPrice', suffix: '', numberPrecision: 0 })
    },
    {
      featureName: 'Bandwidth/mo',
      featureDescription: 'Price per extra GB of bandwidth per month',
      featureIcon: 'faGauge',
      handleFunction: HANDLE_FUNCTIONS.asPriceWithSuffix({ field: 'overageBandwidthPrice', suffix: ' /GB', numberPrecision: 2 })
    }
  ],
  [
    {
      featureName: 'Pro Icons',
      featureDescription: 'Full Pro icon library',
      featureIcon: 'faIcons',
      handleFunction: HANDLE_FUNCTIONS.asDeepFieldMatch({ field: 'iconCollection name', match: ['pro-collection', 'pro-plus-collection'] })
    },
    {
      featureName: 'Pro+ Icons',
      featureDescription: 'Full Pro+ icon library',
      featureIcon: 'faShapes',
      handleFunction: HANDLE_FUNCTIONS.asDeepFieldMatch({ field: 'iconCollection name', match: ['pro-plus-collection'] })
    },
    {
      featureName: 'Icon Download',
      featureDescription: 'Download icons for offline (and online) use',
      featureIcon: 'faArrowDownToLine',
      handleFunction: HANDLE_FUNCTIONS.asBoolean({ field: 'proDownloadEnabled' })
    },
    {
      featureName: 'Hosted npm',
      featureDescription: 'Hosted package registries (npm, Yarn)',
      featureIcon: 'faBox',
      handleFunction: HANDLE_FUNCTIONS.asBoolean({ field: 'proDownloadEnabled' })
    },
    {
      featureName: 'Perpetual License',
      featureDescription: 'Keep icons released during your subscription',
      featureIcon: 'faFile',
      handleFunction: HANDLE_FUNCTIONS.asBoolean({ field: 'perpetualLicenseEnabled' })
    },
    {
      featureName: 'Figma',
      featureDescription: 'Use icons in Figma with our plugin or component file',
      featureIcon: 'faPenNib',
      handleFunction: HANDLE_FUNCTIONS.asBoolean({ field: 'proDownloadEnabled' })
    },
    {
      featureName: 'WordPress',
      featureDescription: 'Official WordPress plugin for easy setup',
      featureIcon: 'faPuzzle',
      // WordPress is included with every plan tier
      handleFunction: () => ({ included: true })
    }
  ]
]
